import { Button, Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { CreateGroupMessageEmailTemplate } from 'src/shared/services/groupMessageService';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GroupMessageEmailTemplateRequest } from '../models/groupMessageEmailTemplateRequest';

type GroupMessageEmailTemplateCreateModalProps = {
  open: boolean;
  openModal: (open: boolean) => void;
  body: string;
};

export const GroupMessageEmailTemplateCreateModal = (props: GroupMessageEmailTemplateCreateModalProps) => {
  const { open, openModal, body } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);

  const minimumCharRequired = 3;
  const maximumCharRequired = 30;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });

  const createGroupMessageEmailTemplateSchema = yup
    .object({
      name: yup
        .string()
        .min(minimumCharRequired, minCharRequiredMessage)
        .max(maximumCharRequired, maxCharRequiredMessage)
        .required(t('common:validationMessages.required')),
      body: yup.string().required(),
    })
    .required();

  const formMethods = useForm<GroupMessageEmailTemplateRequest>({
    resolver: yupResolver(createGroupMessageEmailTemplateSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      body,
    },
  });

  const { handleSubmit, setValue, watch, reset } = formMethods;

  useEffect(() => {
    if (body) {
      setValue('body', body);
    }
  }, [body, setValue]);

  const onSubmit = async (data: GroupMessageEmailTemplateRequest) => {
    setLoading(true);

    try {
      ConsoleHelper.log('data to submit', data);
      await CreateGroupMessageEmailTemplate(data);
      if (isMounted()) {
        toast.success(<>{t('groupMessage:create.templateCreated')}</>);
        reset();
        openModal(false);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          open={open}
          onClose={() => {
            reset();
            openModal(false);
          }}
          onSave={() => ConsoleHelper.log()}
          dialogTitle={t('groupMessage:create.createNewTemplate')}
          maxWidth='sm'
          dialogContent={
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ControlledTextField label={t('groupMessage:create.templateName')} name='name' />
              </Grid>
            </Grid>
          }
          dialogActions={
            <>
              <Button variant='outlined' color='primary' disableElevation onClick={() => openModal(false)}>
                {t('common:button.cancel')}
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={loading || !watch('body')}
                autoFocus
                variant='contained'
                color='primary'
                disableElevation
              >
                {t('common:button.save')}
              </Button>
            </>
          }
        />
      </form>
    </FormProvider>
  );
};
